import {
  Fragment,
  useEffect,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { ArticleReadingLayout } from '~/components/sections/ArticleReadingLayout';
import { CtaBanner } from '~/components/sections/CtaBanner';
import { Grid } from '~/components/ui';
import type { LegalBlock, LegalHeadingLevel, LegalPage } from '~/content/legal';
import type { ArticleInline, ArticleListBlock, ArticleTableCell } from '~/content/learn';
import { safeArticleHref } from '~/lib/article-richtext';
import { cn } from '~/lib/cn';

const OVERVIEW_ID = 'overview';
const TOC_INSET_PX = 20;

interface Segment {
  id: string;
  heading: string | null;
  level: LegalHeadingLevel | null;
  blocks: Exclude<LegalBlock, { type: 'heading' }>[];
}

function slugifyHeading(value: string): string {
  return value
    .toLowerCase()
    .replace(/[’'"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function uniqueSlug(base: string, used: Set<string>): string {
  const root = base || 'section';
  if (!used.has(root)) {
    used.add(root);
    return root;
  }
  let n = 2;
  while (used.has(`${root}-${n}`)) n += 1;
  const id = `${root}-${n}`;
  used.add(id);
  return id;
}

/**
 * Split a flat legal body into an intro (before the first heading) plus one
 * segment per heading — same shape as Learn article TOC anchors.
 */
function toSegments(body: LegalPage['body']): Segment[] {
  const segments: Segment[] = [];
  const usedIds = new Set<string>();
  let current: Segment = {
    id: uniqueSlug(OVERVIEW_ID, usedIds),
    heading: null,
    level: null,
    blocks: [],
  };

  for (const block of body) {
    if (block.type === 'heading') {
      segments.push(current);
      current = {
        id: uniqueSlug(slugifyHeading(block.text), usedIds),
        heading: block.text,
        level: block.level ?? 2,
        blocks: [],
      };
    } else {
      current.blocks.push(block);
    }
  }
  segments.push(current);
  return segments;
}

/** Dense ALL-CAPS legal notices (arbitration, acceptance) get a quiet callout. */
function isImportantNotice(text: string): boolean {
  const letters = text.replace(/[^a-zA-Z]/g, '');
  if (letters.length < 40) return false;
  const upper = letters.replace(/[^A-Z]/g, '').length;
  return upper / letters.length >= 0.85;
}

function InlineRuns({ runs }: { runs: ArticleInline[] }) {
  return (
    <>
      {runs.map((run, index) => {
        if (run.text === '\n') return <br key={index} />;
        let node: ReactNode = run.text;
        if (run.bold) node = <strong className="font-semibold">{node}</strong>;
        if (run.italic) node = <em>{node}</em>;
        const href = safeArticleHref(run.href);
        if (href) {
          const external = /^(?:[a-z]+:)?\/\//i.test(href);
          node = (
            <a
              href={href}
              className="text-blue-600 underline underline-offset-2 transition-colors hover:text-blue-700"
              {...(run.newTab || external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
              {node}
            </a>
          );
        }
        return <Fragment key={index}>{node}</Fragment>;
      })}
    </>
  );
}

function LegalList({ block, nested = false }: { block: ArticleListBlock; nested?: boolean }) {
  const ListTag = block.ordered ? 'ol' : 'ul';
  return (
    <ListTag
      className={cn(
        'flex flex-col gap-3 pl-5 text-body text-grey-900 marker:text-grey-500',
        block.ordered ? 'list-decimal' : 'list-disc',
        nested && 'mt-3',
      )}>
      {block.items.map((item, index) => (
        <li key={`${index}-${item}`} className="pl-1">
          {block.itemsInline?.[index] ? <InlineRuns runs={block.itemsInline[index]} /> : item}
          {block.nestedLists?.[index]?.map((nestedList, nestedIndex) => (
            <LegalList key={nestedIndex} block={nestedList} nested />
          ))}
        </li>
      ))}
    </ListTag>
  );
}

function TableCellContent({ cell }: { cell: ArticleTableCell }) {
  return cell.inline ? <InlineRuns runs={cell.inline} /> : cell.text;
}

function Block({ block }: { block: Exclude<LegalBlock, { type: 'heading' }> }) {
  switch (block.type) {
    case 'paragraph':
      if (isImportantNotice(block.text)) {
        return (
          <aside className="rounded-xl border border-black-6 bg-grey-25 px-5 py-5 md:px-6 md:py-6">
            <p className="text-body-s leading-relaxed text-grey-900 md:text-body">
              {block.inline ? <InlineRuns runs={block.inline} /> : block.text}
            </p>
          </aside>
        );
      }
      return (
        <p className="text-body text-grey-900">
          {block.inline ? <InlineRuns runs={block.inline} /> : block.text}
        </p>
      );
    case 'list':
      return <LegalList block={block} />;
    case 'table': {
      const headerCells: ArticleTableCell[] =
        block.headerCells ??
        block.columns.map((text) => ({ text, header: true, scope: 'col' as const }));
      const bodyRows: ArticleTableCell[][] =
        block.bodyRows ?? block.rows.map((row) => row.map((text) => ({ text })));
      const isSingleColumn = headerCells.length === 1;
      const headerKey = headerCells
        .map((cell) => cell.text)
        .join(' ')
        .toLowerCase();
      // Schumer-style disclosure / fee tables: two columns, label + amount.
      const isDisclosureTable =
        headerCells.length === 2 &&
        (headerCells.every((cell) => !cell.text.trim()) ||
          headerKey === 'item amount' ||
          /rate|fee|charge|apr|amount/.test(headerKey));

      if (isDisclosureTable) {
        return (
          <div className="overflow-hidden rounded-xl border border-black-6">
            <table className="w-full border-collapse text-left text-body text-grey-900">
              <tbody>
                {bodyRows.map((row, rowIndex) => (
                  <tr
                    key={`${row.map((cell) => cell.text).join('|')}-${rowIndex}`}
                    className="border-b border-black-6 last:border-b-0">
                    <th
                      scope="row"
                      className="w-[58%] bg-grey-25 px-4 py-4 align-top font-medium text-black md:px-5">
                      {row[0] ? <TableCellContent cell={row[0]} /> : null}
                    </th>
                    <td className="px-4 py-4 align-top font-semibold tabular-nums text-black md:px-5">
                      {row[1] ? <TableCellContent cell={row[1]} /> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      return (
        <table className="w-full border-collapse text-left text-body text-grey-900">
          {headerCells.length > 0 ? (
            <thead>
              <tr className="border-b border-black-6">
                {headerCells.map((cell, index) => (
                  <th
                    key={`${cell.text}-${index}`}
                    scope={cell.scope ?? 'col'}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                    className="py-3 pr-6 align-top font-semibold text-black last:pr-0">
                    <TableCellContent cell={cell} />
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr
                key={`${row.map((cell) => cell.text).join('|')}-${rowIndex}`}
                className="border-b border-black-6 last:border-b-0">
                {row.map((cell, cellIndex) => (
                  <Fragment key={cellIndex}>
                    {cell.header ? (
                      <th
                        scope={cell.scope ?? 'row'}
                        colSpan={cell.colSpan}
                        rowSpan={cell.rowSpan}
                        className="w-[38%] py-3 pr-6 align-top font-medium text-black">
                        <TableCellContent cell={cell} />
                      </th>
                    ) : (
                      <td
                        colSpan={cell.colSpan}
                        rowSpan={cell.rowSpan}
                        className={
                          isSingleColumn
                            ? 'py-3 align-top'
                            : cellIndex === 0
                              ? 'w-[38%] py-3 pr-6 align-top font-medium text-black'
                              : 'py-3 align-top'
                        }>
                        <TableCellContent cell={cell} />
                      </td>
                    )}
                  </Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  }
}

function SectionHeading({ level, children }: { level: LegalHeadingLevel; children: string }) {
  if (level === 3) {
    return (
      <h3 className="text-body font-semibold text-black md:text-h5 md:tracking-[-0.02em]">
        {children}
      </h3>
    );
  }
  if (level === 4) {
    return <h4 className="text-body font-semibold text-black">{children}</h4>;
  }
  return (
    <h2 className="text-h5 font-semibold tracking-[-0.02em] text-black md:text-h4">{children}</h2>
  );
}

function ArticleToc({
  items,
  activeId,
  onNavigate,
  className,
}: {
  items: { id: string; label: string; insetPx: number }[];
  activeId: string;
  onNavigate: (event: MouseEvent<HTMLAnchorElement>, id: string) => void;
  className?: string;
}) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="On this page" className={cn('group/toc flex flex-col gap-1', className)}>
      {items.map((item, i) => {
        const isActive = item.id === activeId;
        return (
          <a
            key={item.id}
            href={`#${item.id}`}
            aria-current={isActive ? 'true' : undefined}
            onClick={(event) => onNavigate(event, item.id)}
            style={
              {
                '--stagger': `${i * 40}ms`,
                paddingLeft: item.insetPx ? `${item.insetPx}px` : undefined,
              } as CSSProperties
            }
            className={cn(
              'toc-item-in py-1.5 text-body-s transition-colors ease-nav duration-150 group-has-[a:hover]/toc:text-grey-400 hover:!text-grey-1000',
              isActive ? 'font-medium text-grey-1000' : 'text-grey-500',
            )}>
            {item.label}
          </a>
        );
      })}
    </nav>
  );
}

/** Shared chrome for legal / policy reading pages (single article column). */
export function LegalPageFrame({ children }: { children: ReactNode }) {
  return (
    <PageShell>
      <Section className="pt-16 pb-[240px] md:pt-24 md:pb-[240px]">
        <Grid>
          <ArticleReadingLayout>{children}</ArticleReadingLayout>
        </Grid>
      </Section>
      <CtaBanner />
    </PageShell>
  );
}

/** Privacy-policy-style reading layout for a single legal document, with in-article TOC. */
export function LegalDocument({ page }: { page: LegalPage }) {
  const segments = toSegments(page.body);
  const [intro, ...sections] = segments;

  // Body section headings only — never the document H1 / intro overview.
  const tocMinLevel = sections.reduce<number>(
    (min, segment) => Math.min(min, segment.level ?? 2),
    4,
  );
  const tocItems = sections
    .filter((segment) => segment.heading)
    .map((segment) => {
      const level = segment.level ?? 2;
      return {
        id: segment.id,
        label: segment.heading ?? '',
        insetPx: level > tocMinLevel ? (level - tocMinLevel) * TOC_INSET_PX : 0,
      };
    });
  const hasToc = tocItems.length > 0;
  const firstTocId = tocItems[0]?.id ?? OVERVIEW_ID;

  const [activeId, setActiveId] = useState(firstTocId);

  useEffect(() => {
    setActiveId(firstTocId);
  }, [page.fullPath, firstTocId]);

  const tocIdsKey = tocItems.map((item) => item.id).join('|');

  useEffect(() => {
    // Only spy on section headings listed in the TOC (skip the H1 / intro block).
    const ids = tocIdsKey ? tocIdsKey.split('|') : [];
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length === 0) return;
        const topmost = visible.reduce((a, b) =>
          a.boundingClientRect.top <= b.boundingClientRect.top ? a : b,
        );
        setActiveId(topmost.target.id);
      },
      { rootMargin: '-96px 0px -65% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [page.fullPath, tocIdsKey]);

  const handleTocClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <PageShell>
      <Section className="pt-16 pb-[240px] md:pt-24 md:pb-[240px]">
        <Grid>
          <ArticleReadingLayout
            toc={
              hasToc ? (
                <ArticleToc
                  items={tocItems}
                  activeId={activeId}
                  onNavigate={handleTocClick}
                  className="lg:mt-[52px]"
                />
              ) : undefined
            }>
            <Link
              to="/terms-conditions"
              className="group inline-flex items-center gap-2 text-body-s font-medium text-red-500 transition-opacity duration-150 ease-nav hover:opacity-70">
              <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
                <path
                  d="M7.68115 18.5L2.86084 12.5674L2.3999 12L2.86084 11.4326L7.68115 5.5L10.0005 5.5L4.71924 12L10.0005 18.5L7.68115 18.5Z"
                  fill="currentColor"
                />
                <path d="M4 12H21.5" stroke="currentColor" strokeWidth={1.8} />
              </svg>
              <span className="link-underline-lr bg-origin-content">Back</span>
            </Link>

            {hasToc ? (
              <details className="mt-8 mb-2 rounded-xl border border-black-6 bg-white lg:hidden">
                <summary className="cursor-pointer list-none px-4 py-3 text-body-s font-medium text-grey-1000 [&::-webkit-details-marker]:hidden">
                  On this page
                </summary>
                <div className="border-t border-black-6 px-4 py-3">
                  <ArticleToc items={tocItems} activeId={activeId} onNavigate={handleTocClick} />
                </div>
              </details>
            ) : null}

            <section id={intro.id} className="mt-8 scroll-mt-24">
              <h1 className="text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
                {page.title}
              </h1>
              {page.lastUpdated ? (
                <p className="mt-6 text-body-m text-grey-500">Last updated: {page.lastUpdated}</p>
              ) : null}
              {intro.blocks.length > 0 ? (
                <div className="mt-10 space-y-6">
                  {intro.blocks.map((block, index) => (
                    <Block key={`${block.type}-${index}`} block={block} />
                  ))}
                </div>
              ) : null}
            </section>

            {sections.length > 0 ? (
              <div className="mt-[96px]">
                {sections.map((segment, index) => {
                  const level = segment.level ?? 2;
                  const isH2 = level === 2;
                  const isH4 = level === 4;
                  return (
                    <section
                      key={segment.id}
                      id={segment.id}
                      data-toc-target
                      className={cn(
                        'scroll-mt-24',
                        index > 0 && (isH2 ? 'mt-[96px]' : isH4 ? 'mt-14' : 'mt-[72px]'),
                      )}>
                      <SectionHeading level={level}>{segment.heading ?? ''}</SectionHeading>
                      {segment.blocks.length > 0 ? (
                        <div className={cn('space-y-6', isH4 ? 'mt-5' : 'mt-8')}>
                          {segment.blocks.map((block, blockIndex) => (
                            <Block key={`${block.type}-${blockIndex}`} block={block} />
                          ))}
                        </div>
                      ) : null}
                    </section>
                  );
                })}
              </div>
            ) : null}
          </ArticleReadingLayout>
        </Grid>
      </Section>
      <CtaBanner />
    </PageShell>
  );
}
